// Card action menu, context menu actions, and the inspector stack,
// extracted from GameBoard.jsx. openCardActionMenu assembles the
// per-card action list (tap/flip/statuses/abilities/equip/combat
// entries) from the host's current rules state; the inspector stack
// supports drilling into equipped cards and opponent hand reveals.

import { canBurrow, canSubmerge, canSurface, getRangedValue, hasBurrowing, hasSubmerge, hasSummoningSickness, isArtifact, isBurrowed, isSubmerged } from '../../../utils/game/engine/core';
import { activatedMenuLabel } from '../../../utils/game/engine/automation';
import { hasKeyword } from '../../../utils/game/engine/core/keywordQuery';
import { resolveLocalImageUrl } from '../../../utils/localApi';
import { t } from '../../../localization/t';

// Build + show the context-aware action menu for `cardInstance`,
// anchored at the cursor (event x/y). Menu items are filtered by
// eligibility. Burrow/submerge/surface only appear when valid per
// the codex rules.
export function openCardActionMenu(host, cardInstance, event) {
  if (!cardInstance) return;
  // Manual (automations-off) session: the menu stays. It is the only path
  // to burrow / submerge / surface. But it holds ONLY physical actions
  // (subsurface rows for every non-site card, tap). Activate / Attack /
  // Shoot are the engine acting on the players' behalf and drop out.
  const manual = host._automationsEnabled?.() === false;
  const mesh = host.meshes.get(cardInstance.id);
  if (!mesh) return;
  const cell = host._cellAtWorld(mesh.position.x, mesh.position.z);
  const getFullCard = host._getFullCard;
  const fullCard = getFullCard(cardInstance.cardId);

  // v1.34. The menu ALWAYS shows the full action set; unavailable
  // actions are rendered disabled (greyed-out, fully opaque) so the
  // menu's vertical rhythm stays consistent across cards. Labels
  // never include parenthetical hints like "(will die)." Keeping
  // them single-word means every button fits on one line and the
  // stack auto-sizes to the longest label.
  const isLocal = host.isCardOnLocalSide(cardInstance);
  const col = cell?.col ?? 0;
  const row = cell?.row ?? 0;
  const activatedEntries = host._activatedEntriesFor(cardInstance);

  const items = [];

  // Activated abilities. The avatar's built-in Tap-to-draw-a-site
  // plus any parser-emitted or rules-declared extras. A single entry
  // shows as its own button (e.g. plain Avatar → "Draw a site");
  // multiple entries collapse into one "Activate" button that opens
  // a sub-menu so the player picks which tap effect to spend the
  // avatar's tap on (Sorcerer's "Draw a site" + "Draw a spell").
  const activatedItems = activatedEntries.map((entry, idx) => ({
    id: entry._builtin ? `act-${entry._builtin}` : `act-${entry.id || idx}`,
    label: activatedMenuLabel(entry),
    disabled: !(isLocal && host._canActivateEntry(cardInstance, entry)),
    onClick: () => host._activateAbility(cardInstance, entry),
  }));
  if (activatedItems.length === 1) {
    // A single ability surfaces as its own button. activatedMenuLabel
    // keeps it terse. Built-ins carry their authored label ("Draw a
    // site"), parser abilities collapse to a curated or four-word label
    // so a long clause never overflows the menu width.
    items.push(activatedItems[0]);
  } else if (activatedItems.length >= 2) {
    items.push({
      id: 'activate',
      label: t('Activate'),
      disabled: activatedItems.every((it) => it.disabled),
      subItems: activatedItems,
    });
  }

  // Equipped artifacts' activated abilities surface on the BEARER's
  // menu. The equipment has no mesh of its own to right-click.
  // Cradle of Etherrum's "look at your next spell" is the canonical
  // case when the Relic is carried instead of field-placed.
  for (const eq of cardInstance.equippedCards || []) {
    const eqEntries = host._activatedEntriesFor(eq);
    eqEntries.forEach((entry, idx) => {
      items.push({
        id: `eq-act-${eq.id}-${idx}`,
        label: t('{name}: {ability}', { name: eq.name || t('Equipment'), ability: activatedMenuLabel(entry) }),
        disabled: !(isLocal && host._canActivateEntry(eq, entry)),
        onClick: () => host._activateAbility(eq, entry),
      });
    });
  }

  // Subsurface toggles. Burrow and Submerge each double as the way
  // back up: when the unit is currently in that region the button
  // re-labels to "Surface" and surfaces on click. This replaces a
  // separate Surface row and naturally enforces the codex rule that
  // a unit can occupy only one subsurface region. The *other*
  // toggle disables itself while the unit is below.
  //
  // Eligibility uses canBurrow / canSubmerge (keyword-aware via
  // hasKeyword → respects radial-menu grants AND suppressions) so
  // adding or stripping a keyword from the radial ring is reflected
  // the next time the player opens the action menu.
  //
  // Sites are the landscape itself and can't move between regions,
  // so the rows are omitted entirely on a site.
  if (!cardInstance.isSite) {
    const burrowed = isBurrowed(cardInstance);
    const submerged = isSubmerged(cardInstance);
    const surfaceHere = !!cell && isLocal
      && canSurface(cardInstance, host.meshes, host._cellAtWorld, col, row, getFullCard);
    // Artifacts always get both rows. They survive any subsurface
    // region (the codex death rules name minions) and unautomated
    // cards that burrow/submerge artifacts need a manual path.
    const artifact = isArtifact(cardInstance);
    // Codex: a unit's burrow / submerge / surface is a STEP of its Move and
    // Attack. A tapped or summoning-sick unit can't pay the tap cost, so
    // its rows disable exactly like a drag-move would be rejected. Artifacts
    // stay ungated (their rows are the forced-movement manual path).
    const canStep = artifact || (!cardInstance.tapped && !hasSummoningSickness(cardInstance));

    // Burrow toggle. Shown when the unit has the keyword OR is
    // already burrowed (so it always has a way back up). Disabled
    // when submerged (must surface first; can't be in two regions).
    // Manual session: EVERY non-site card gets both rows, ungated.
    // the players own the keyword and cell-type rules.
    if (manual || hasBurrowing(cardInstance) || burrowed || artifact) {
      const burrowAvail = !!cell && isLocal && !submerged && canStep
        && canBurrow(cardInstance, host.meshes, host._cellAtWorld, col, row, getFullCard);
      items.push({
        id: 'burrow',
        label: burrowed ? t('Surface') : t('Burrow'),
        disabled: manual ? !isLocal
          : (burrowed ? !(surfaceHere && canStep) : !burrowAvail),
        onClick: () => host._voluntarySubsurfaceMove(cardInstance, burrowed ? null : 'underground'),
      });
    }

    // Submerge toggle. Symmetric to Burrow. Disabled when burrowed.
    if (manual || hasSubmerge(cardInstance) || submerged || artifact) {
      const submergeAvail = !!cell && isLocal && !burrowed && canStep
        && canSubmerge(cardInstance, host.meshes, host._cellAtWorld, col, row, getFullCard);
      items.push({
        id: 'submerge',
        label: submerged ? t('Surface') : t('Submerge'),
        disabled: manual ? !isLocal
          : (submerged ? !(surfaceHere && canStep) : !submergeAvail),
        onClick: () => host._voluntarySubsurfaceMove(cardInstance, submerged ? null : 'underwater'),
      });
    }
  }
  // Attack in place. Codex Attack: "You may only attack enemy units or
  // the surface of enemy sites that are at your location." No movement
  // is spent; the target modal opens above the unit's own square
  // (_beginCombatAction 'attack' → _openCombatTargetPrompt with a zero
  // step budget). Shown for every local combatant, disabled when the
  // unit can't act right now (tapped / sick / disabled / not its
  // controller's turn). Same _canBeginCombat gate as the right-click
  // context menu. 0 power is NOT a gate: a powerless unit may still attack.
  const combatGate = host._canBeginCombat(cardInstance);
  // Manual session: no combat resolution, so no Attack / Shoot rows.
  if (combatGate.combatant && !manual) {
    items.push({
      id: 'attack',
      label: t('Attack'),
      disabled: !combatGate.canAttack,
      onClick: () => host._beginCombatAction(cardInstance, 'attack'),
    });
    // Codex Ranged: shooting is a separate activated ability ("Tap →
    // Shoot a projectile that stops after 1 step. Strike the hit
    // unit."), not part of Move and Attack. Same gates as Attack;
    // mirrors the right-click context menu's Shoot item.
    const rangedValue = getRangedValue(cardInstance);
    if (rangedValue > 0) {
      items.push({
        id: 'shoot',
        label: t('Shoot (Ranged {n})', { n: rangedValue }),
        disabled: !combatGate.canAttack,
        onClick: () => host._beginCombatAction(cardInstance, 'shoot'),
      });
    }
  }

  items.push({
    id: 'tap',
    label: cardInstance.tapped ? t('Untap') : t('Tap'),

    disabled: false, // tap is always available (was the old default)
    onClick: () => host.tapCard(cardInstance, mesh),
  });

  // Project the card's screen-space bounding box so the menu can
  // anchor to either side and clamp vertically. Fallback (no mesh
  // hit) collapses the bbox to the click point so the menu still
  // opens, just at the cursor.
  const projected = host._projectCardRightEdge(mesh);
  const cardBounds = projected || {
    cardLeft: event.clientX,
    cardRight: event.clientX,
    cardTop: event.clientY,
    cardBottom: event.clientY,
  };

  host.setState({
    cardActionMenu: {
      cardId: cardInstance.id,
      cardName: fullCard?.name || cardInstance.name || '',
      cardBounds,
      items,
    },
    // Clear the hover stack immediately so status/damage badges +
    // equipment peek vanish for the host card while the menu is up.
    hoverStatusOverlay: null,
  });
}

export function closeCardActionMenu(host) {
  if (!host.state.cardActionMenu) return;
  host.setState({ cardActionMenu: null });
}

export function pushInspector(host, equippedCard) {
  if (!equippedCard) return;
  host.setState((state) => ({
    inspectorStack: [...state.inspectorStack, state.inspectedCard],
    inspectedCard: equippedCard,
  }));
}

// Open the inspector for a card the opponent has revealed from their
// hand. The card payload is the broadcast snapshot from `hand:info`
// (only `cardId`, `name`, `imageUrl`, `foiling`, `isSite`), tagged
// here so the `hand:info` sync can auto-close the inspector when the
// opponent later hides or plays the card.
export function openOpponentHandInspector(host, cardData) {
  if (!cardData) return;
  host.setState({
    inspectedCard: { ...cardData, _fromOpponentHand: true },
    inspectorStack: [],
  });
  host.scene?.setPaused(true);
  host._presenceHover(null);
}

export function popInspector(host) {
  host.setState((state) => {
    if (state.inspectorStack.length === 0) return null;
    const stack = state.inspectorStack.slice(0, -1);
    const prev = state.inspectorStack[state.inspectorStack.length - 1];
    return { inspectorStack: stack, inspectedCard: prev };
  });
}

// App.jsx consults this before opening its own escape menu. When an
// in-game overlay is up, GameBoard's handler closes it instead.
export function hasOpenOverlay(host) {
  if (host.marquee) return true;
  if (host.selectedCardIds?.size > 0) return true;
  const s = host.state;
  return Boolean(
    s.contextMenu ||
    s.ringMenu ||
    s.showGameMenu ||
    s.showDiceMenu ||
    s.showAmbienceMenu ||
    s.showPlaymatPicker ||
    s.showCodex ||
    s.showShortcutHelp ||
    s.showExitConfirm ||
    s.showDeckPicker ||
    s.showSaveDialog ||
    s.scry ||
    s.searchPile ||
    s.inspectedCard ||
    s.attackTargetModal ||
    s.cardActionMenu
  );
}

// Update the top-right CardPreviewDrawer's focused card. Sticky: a
// null/face-down hover does NOT clear the preview. The last useful
// card stays visible until another face-up card is hovered. This
// matters for streamers: scrubbing the cursor across empty playmat
// shouldn't blank the preview their viewers are reading.
export function setFocusedPreviewCard(host, cardInstance) {
  if (!cardInstance || cardInstance.faceDown) return;
  // Card instances carry the absolute (private Blob) image_url. DOM <img>
  // tags must use the root-relative proxied path so the local server can
  // serve the image from the current origin. Same as every other DOM card
  // image (see cardPileLifecycle.js). Without this the drawer renders a raw
  // private-Blob URL the browser can't load, so the preview shows nothing.
  const imageUrl = resolveLocalImageUrl(cardInstance.imageUrl) || null;
  if (!imageUrl) return;
  const prev = host.state.focusedPreviewCard;
  if (prev && prev.imageUrl === imageUrl) return;
  host.setState({
    focusedPreviewCard: {
      imageUrl,
      name: cardInstance.name || null,
      cardId: cardInstance.cardId || null,
      foiling: cardInstance.foiling || null,
      // Sites are stored portrait but played landscape. The drawer
      // renders them rotated 90° clockwise. Board/hand instances
      // carry `isSite`; catalog rows from logbook mentions carry
      // `played_horizontally` instead.
      isSite: !!(cardInstance.isSite || cardInstance.played_horizontally),
    },
  });
}

// Opens the full-card inspector for whatever the drawer currently
// shows. The drawer-stored shape mirrors the inspector's expected
// fields (cardId, imageUrl, foiling) so we can pass it through
// directly. Inspector reads statuses/equippedCards from the
// instance if present and defaults them to empty otherwise. Fine
// for a streamer "what is this card" lookup.
export function openInspectorForFocusedCard(host) {
  const focused = host.state.focusedPreviewCard;
  if (!focused) return;
  host.setState({ inspectedCard: focused, inspectorStack: [] });
  host.scene?.setPaused(true);
}
